// The "Day N of chess but…" schedule. Each day at midnight New York time the next scheduled
// variant (if it exists and isn't a draft) becomes today's variant. Days only advance when
// there's a variant for them, so building ahead is safe and missing a day just keeps the
// previous variant featured. Daily leaderboards reset at the same midnight.

export interface CampaignDay {
  day: number;
  variant: string;
  /** Hidden in production until the flag is removed (admins can still preview it). */
  draft?: boolean;
  /** Bot level for that day's daily challenge (default 3). */
  challengeLevel?: number;
}

export const CAMPAIGN = {
  timeZone: 'America/New_York',
  /** Day 1, as YYYY-MM-DD in `timeZone`. Move this to the day the first reel goes out. */
  startDate: '2026-10-16',
  days: [
    { day: 1, variant: 'snail' },
    { day: 2, variant: 'explode', draft: true },
    { day: 3, variant: 'bounce', draft: true },
    { day: 4, variant: 'storm', draft: true },
    { day: 5, variant: 'goose', draft: true },
    { day: 6, variant: 'cooldown', draft: true },
    { day: 7, variant: 'hp', draft: true },
    { day: 8, variant: 'lava', draft: true },
    { day: 9, variant: 'levelup', draft: true },
    { day: 10, variant: 'wrap', draft: true },
    { day: 11, variant: 'zombie', draft: true },
    { day: 12, variant: 'portal', draft: true },
    { day: 13, variant: 'traitor', draft: true },
    { day: 14, variant: 'flip', draft: true },
    { day: 15, variant: 'duel', draft: true },
    { day: 16, variant: 'wildcard', draft: true },
    { day: 17, variant: 'masquerade', draft: true },
    { day: 18, variant: 'grey', draft: true },
    { day: 19, variant: 'comeback', draft: true },
    { day: 20, variant: 'heir', draft: true },
  ] as CampaignDay[],
  /** Always playable, never featured ("Day 0"). */
  extras: ['classic'],
};

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string) {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(tz, f);
  }
  return f;
}

function zonedParts(ms: number, tz: string) {
  const out: Record<string, number> = {};
  for (const p of formatter(tz).formatToParts(new Date(ms))) if (p.type !== 'literal') out[p.type] = Number(p.value);
  return out as { year: number; month: number; day: number; hour: number; minute: number; second: number };
}

/** Offset of `tz` from UTC at instant `ms`, in ms (New York in summer: -4 h). */
function tzOffset(ms: number, tz: string): number {
  const p = zonedParts(ms, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Calendar day (YYYY-MM-DD) of an instant in the campaign time zone. */
export function dayKey(ms = Date.now(), tz = CAMPAIGN.timeZone): string {
  const p = zonedParts(ms, tz);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

function keyToUtc(key: string): number {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

export function addDays(key: string, n: number): string {
  const t = new Date(keyToUtc(key) + n * 86_400_000);
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

export function daysBetweenKeys(from: string, to: string): number {
  return Math.round((keyToUtc(to) - keyToUtc(from)) / 86_400_000);
}

/** The instant a calendar day starts (00:00) in the campaign time zone. */
export function dayStart(key: string, tz = CAMPAIGN.timeZone): number {
  const guess = keyToUtc(key);
  let t = guess - tzOffset(guess, tz);
  t = guess - tzOffset(t, tz);
  return t;
}

/** Campaign day number for an instant: 1 on the start date, 0 or less before it. */
export function campaignDayNumber(ms = Date.now()): number {
  return daysBetweenKeys(CAMPAIGN.startDate, dayKey(ms)) + 1;
}

export function releaseKey(day: number): string {
  return addDays(CAMPAIGN.startDate, day - 1);
}

export function variantDay(variant: string): number | null {
  if (CAMPAIGN.extras.includes(variant)) return 0;
  return CAMPAIGN.days.find((d) => d.variant === variant)?.day ?? null;
}

export interface ReleasedVariant {
  variant: string;
  /** Campaign day, 0 for extras like classic chess. */
  day: number;
  /** Day it came out (YYYY-MM-DD), null for extras. */
  releasedOn: string | null;
  draft: boolean;
}

export interface CampaignState {
  /** Today in the campaign time zone (YYYY-MM-DD): the daily leaderboard's key. */
  today: string;
  /** Campaign day number today (can exceed the latest released day). */
  dayNumber: number;
  /** Today's featured variant: the newest released day. */
  featured: ReleasedVariant | null;
  /** Every playable variant, newest day first, extras last. */
  released: ReleasedVariant[];
  /** When the next day starts (ms), for countdowns and leaderboard resets. */
  nextDayAt: number;
  /** Whether a variant is scheduled for the next day (kept secret: no id). */
  nextDayHasVariant: boolean;
  challengeLevel: number;
}

/** What's playable right now. `preview` includes drafts and future days (admins). */
export function campaignState(ms = Date.now(), preview = false): CampaignState {
  const today = dayKey(ms);
  const dayNumber = campaignDayNumber(ms);
  const scheduled = CAMPAIGN.days
    .filter((d) => preview || (!d.draft && d.day <= dayNumber))
    .sort((a, b) => b.day - a.day);
  const released: ReleasedVariant[] = scheduled.map((d) => ({
    variant: d.variant,
    day: d.day,
    releasedOn: releaseKey(d.day),
    draft: !!d.draft,
  }));
  const live = released.filter((r) => r.day <= dayNumber && !r.draft);
  const featured = live[0] ?? null;
  for (const v of CAMPAIGN.extras) released.push({ variant: v, day: 0, releasedOn: null, draft: false });
  const next = CAMPAIGN.days.find((d) => d.day === dayNumber + 1);
  const featuredDay = featured ? CAMPAIGN.days.find((d) => d.day === featured.day) : undefined;
  return {
    today,
    dayNumber,
    featured,
    released,
    nextDayAt: dayStart(addDays(today, 1)),
    nextDayHasVariant: !!next && !next.draft,
    // Levels 1–3 search to a fixed depth, so the seeded challenge bot is deterministic and the
    // server can replay its moves to check a submitted game. Higher levels use a time budget.
    challengeLevel: Math.min(3, Math.max(1, featuredDay?.challengeLevel ?? 3)),
  };
}

export function isPlayable(variant: string, ms = Date.now(), preview = false): boolean {
  return campaignState(ms, preview).released.some((r) => r.variant === variant);
}

export function isFeatured(variant: string, ms = Date.now()): boolean {
  return campaignState(ms).featured?.variant === variant;
}

/** Seed for the daily challenge bot, identical on every device (FNV-1a of the day key). */
export function challengeSeed(day: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < day.length; i++) {
    h ^= day.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
